import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { LeadRoutingClient, MyRouting } from "@clients/leadRoutingClient"
import { PROGRAM_LABEL } from "./leadRoutingLabels"
import { AsOf, relativeTime, ValuesChip } from "./freshness"
import { HubSpotLink } from "./hubspotLink"

type SortField = "routedAt" | "qtfScore" | "ruleName" | "confirmed"

// A consultant's personal view: their standing right now, the rules that send
// them leads, and the leads they received as a simple working queue: pick a
// date range, sort by what matters (newest, QTF priority, rule, confirmed),
// work through it. Read-only; rules are managed on the Routing tab by admins.
export function MyRoutingPanel() {
  const todayIso = new Date().toISOString().slice(0, 10)
  const weekAgoIso = new Date(Date.now() - 7 * 86400_000)
    .toISOString()
    .slice(0, 10)
  const [from, setFrom] = useState(weekAgoIso)
  const [to, setTo] = useState(todayIso)
  const [view, setView] = useState<MyRouting>()
  const [error, setError] = useState(false)
  const [fetchedAt, setFetchedAt] = useState<Date>()
  const [sortField, setSortField] = useState<SortField>("routedAt")
  const [sortAsc, setSortAsc] = useState(false)

  const load = useCallback(() => {
    if (!from || !to || from > to) return
    LeadRoutingClient.getMyRouting(from, to)
      .then((next) => {
        setView(next)
        setFetchedAt(new Date())
      })
      .catch(() => setError(true))
  }, [from, to])
  useEffect(() => {
    load()
  }, [load])

  const sorted = useMemo(() => {
    const rows = [...(view?.received ?? [])]
    rows.sort((a, b) => {
      let cmp = 0
      if (sortField === "routedAt") cmp = a.routedAt.localeCompare(b.routedAt)
      if (sortField === "qtfScore") cmp = (a.qtfScore ?? 9) - (b.qtfScore ?? 9)
      if (sortField === "ruleName")
        cmp = (a.ruleName ?? "").localeCompare(b.ruleName ?? "")
      if (sortField === "confirmed")
        cmp = Number(a.confirmed) - Number(b.confirmed)
      return sortAsc ? cmp : -cmp
    })
    return rows
  }, [view, sortField, sortAsc])

  const sortBy = (field: SortField) => {
    if (field === sortField) {
      setSortAsc(!sortAsc)
    } else {
      setSortField(field)
      // QTF queues read best best-first (1 on top); time queues newest-first.
      setSortAsc(field === "qtfScore")
    }
  }

  if (error) {
    return <Alert severity="error">Could not load your routing view.</Alert>
  }
  if (!view) {
    return (
      <Stack alignItems="center" py={8}>
        <CircularProgress />
      </Stack>
    )
  }
  if (!view.consultant) {
    return (
      <Alert severity="info">
        No consultant profile is linked to your account, so the routing engine
        does not assign you leads. Ask a sales admin if that seems wrong.
      </Alert>
    )
  }

  const header = (field: SortField, label: string) => (
    <TableCell
      sortDirection={sortField === field ? (sortAsc ? "asc" : "desc") : false}
    >
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? (sortAsc ? "asc" : "desc") : "desc"}
        onClick={() => sortBy(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Typography variant="subtitle1">{view.consultant.name}</Typography>
          {view.onShiftNow ? (
            <Chip size="small" color="success" label="On shift now" />
          ) : (
            <Chip
              size="small"
              label={
                view.minutesToNextShift !== null
                  ? `Next shift in ${Math.round(
                      view.minutesToNextShift / 60,
                    )}h ${view.minutesToNextShift % 60}m`
                  : "Off shift"
              }
            />
          )}
          {!view.consultant.isAvailable && (
            <Tooltip title="You are marked unavailable, so no leads are assigned to you even on shift.">
              <Chip size="small" color="warning" label="Unavailable" />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          You take QTF{" "}
          {[...view.consultant.acceptedQtfs].sort().join(", ") || "(none set)"}{" "}
          · {view.consultant.languages.join(", ") || "no languages set"}
        </Typography>
      </Paper>

      <Box>
        <Typography variant="subtitle1" mb={0.5}>
          Rules that send you leads
        </Typography>
        {view.rules.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No published rule names you directly. You still receive leads
            through the Everyone else rotation when you are on shift.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {view.rules.map((rule) => (
              <Paper
                key={rule.ruleId}
                variant="outlined"
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">{rule.ruleName}</Typography>
                <Tooltip title="Your current share of this rule's leads when everyone in its split is available. For a tag-based rule this shifts as consultants are tagged, untagged or excluded.">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: "help" }}
                  >
                    your share {rule.targetPct}%
                  </Typography>
                </Tooltip>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          mb={0.5}
        >
          <Typography variant="subtitle1">
            Leads you received · {view.receivedCount}
          </Typography>
          <TextField
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            inputProps={{ max: to, style: { fontSize: 13 } }}
          />
          <Typography variant="caption" color="text.secondary">
            to
          </Typography>
          <TextField
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            inputProps={{ min: from, max: todayIso, style: { fontSize: 13 } }}
          />
          <AsOf fetchedAt={fetchedAt} onRefresh={load} />
        </Stack>
        {sorted.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            None in this range.
          </Typography>
        ) : (
          <Table
            size="small"
            sx={{ "& td, & th": { py: 0.5, fontSize: "0.8rem" } }}
          >
            <TableHead>
              <TableRow>
                {header("routedAt", "When")}
                <TableCell>Lead</TableCell>
                {header("qtfScore", "QTF")}
                <TableCell>Program</TableCell>
                {header("ruleName", "Via")}
                <TableCell>Values used</TableCell>
                {header("confirmed", "Confirmed")}
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((lead, index) => (
                <TableRow key={index}>
                  <TableCell>{relativeTime(lead.routedAt)}</TableCell>
                  <TableCell>{lead.leadName ?? "-"}</TableCell>
                  <TableCell>{lead.qtfScore ?? "-"}</TableCell>
                  <TableCell>
                    {PROGRAM_LABEL[String(lead.program)] ?? lead.program ?? "-"}
                  </TableCell>
                  <TableCell>
                    {lead.ruleName ??
                      (lead.source === "fallback"
                        ? "Everyone else"
                        : lead.source)}
                  </TableCell>
                  <TableCell>
                    <ValuesChip
                      source={lead.propertiesSource}
                      syncedAt={lead.propertiesSyncedAt}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Confirmed means the assignment really landed on the contact in HubSpot.">
                      <span>{lead.confirmed ? "yes" : "pending"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <HubSpotLink email={lead.email} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Stack>
  )
}
